// src/lib/opportunity/formatCalcEval.ts
//
// Pure evaluation for manager-defined calculation lines (7a). Percent
// lines use the running total of all prior quote rows — base amount plus
// earlier lines in the same placement zone (and, for after_subtotal,
// the adjusted subtotal built from base + before-subtotal lines).

import { round2, safeNumber, safePct } from "./calc.js";
import type {
  FormatCalcAmountMode,
  FormatCalcLineDef,
  FormatCalcPlacement,
} from "./formatCalcLineTypes.js";

export type EvaluatedFormatCalcLine = FormatCalcLineDef & {
  /** Resolved dollar amount for this line. */
  amount: number;
};

export type FormatCalcEvaluation = {
  before: EvaluatedFormatCalcLine[];
  after: EvaluatedFormatCalcLine[];
  /** Sum of before-subtotal line amounts. */
  beforeLinesTotal: number;
  /** Sum of after-subtotal line amounts. */
  afterLinesTotal: number;
  /** baseSubtotal + beforeLinesTotal */
  adjustedSubtotal: number;
  /** adjustedSubtotal + afterLinesTotal (pre-tax total for generic format). */
  totalBeforeTax: number;
  /** Sum of commissionable line amounts (both zones). */
  commissionableTotal: number;
  /** Lines visible on the customer proposal. */
  proposalVisibleLines: EvaluatedFormatCalcLine[];
};

function sortLines(lines: FormatCalcLineDef[]): FormatCalcLineDef[] {
  return [...lines].sort((a, b) => a.sort_order - b.sort_order || a.id.localeCompare(b.id));
}

function valueForLine(
  line: FormatCalcLineDef,
  valuesByLineId: Record<string, number> | undefined,
): number {
  if (valuesByLineId && line.id in valuesByLineId) {
    return safeNumber(valuesByLineId[line.id]);
  }
  return safeNumber(line.default_amount);
}

/**
 * Effective amount mode for a line: the per-revision override wins
 * (sales pinning a percent line to exact dollars after a contract
 * change), otherwise the manager-configured mode.
 */
export function effectiveCalcLineMode(
  line: FormatCalcLineDef,
  modesByLineId: Record<string, "fixed" | "percent"> | undefined,
): FormatCalcAmountMode {
  const override = modesByLineId?.[line.id];
  return override === "fixed" || override === "percent" ? override : line.amount_mode;
}

/**
 * Evaluate an ordered stack within one placement zone. Each percent line
 * applies to the running total of everything before it in the quote stack:
 * `initialBasis` (base subtotal for before_subtotal, adjusted subtotal for
 * after_subtotal) plus all prior line amounts in this zone.
 */
export function evaluatePlacementLines(
  lines: FormatCalcLineDef[],
  valuesByLineId?: Record<string, number>,
  initialBasis = 0,
  modesByLineId?: Record<string, "fixed" | "percent">,
): { evaluated: EvaluatedFormatCalcLine[]; zoneTotal: number } {
  const sorted = sortLines(lines);
  let basis = round2(safeNumber(initialBasis));
  let zoneTotal = 0;
  const evaluated: EvaluatedFormatCalcLine[] = [];

  for (const line of sorted) {
    const rawValue = valueForLine(line, valuesByLineId);
    const amount =
      effectiveCalcLineMode(line, modesByLineId) === "percent"
        ? round2(basis * (safePct(rawValue) / 100))
        : round2(safeNumber(rawValue));
    zoneTotal = round2(zoneTotal + amount);
    basis = round2(basis + amount);
    evaluated.push({ ...line, amount });
  }

  return { evaluated, zoneTotal };
}

/**
 * Full evaluation across both placement zones for a quote preview.
 *
 * @param baseSubtotal Rep-entered subtotal (generic) or computed cost
 *   basis (red_iron) before format lines apply.
 */
export function evaluateFormatCalcLines(args: {
  lines: FormatCalcLineDef[];
  baseSubtotal?: number;
  valuesByLineId?: Record<string, number>;
  /** Per-revision $/% overrides (snapshot `format_calc_modes`). */
  modesByLineId?: Record<string, "fixed" | "percent">;
}): FormatCalcEvaluation {
  const base = round2(safeNumber(args.baseSubtotal ?? 0));
  const beforeLines = args.lines.filter((l) => l.placement === "before_subtotal");
  const afterLines = args.lines.filter((l) => l.placement === "after_subtotal");

  const beforeResult = evaluatePlacementLines(
    beforeLines,
    args.valuesByLineId,
    base,
    args.modesByLineId,
  );
  const adjustedSubtotal = round2(base + beforeResult.zoneTotal);
  const afterResult = evaluatePlacementLines(
    afterLines,
    args.valuesByLineId,
    adjustedSubtotal,
    args.modesByLineId,
  );
  const totalBeforeTax = round2(adjustedSubtotal + afterResult.zoneTotal);

  const allEvaluated = [...beforeResult.evaluated, ...afterResult.evaluated];
  const commissionableTotal = round2(
    allEvaluated.filter((l) => l.commissionable).reduce((s, l) => s + l.amount, 0),
  );
  const proposalVisibleLines = allEvaluated.filter((l) => l.visible_on_proposal);

  return {
    before: beforeResult.evaluated,
    after: afterResult.evaluated,
    beforeLinesTotal: beforeResult.zoneTotal,
    afterLinesTotal: afterResult.zoneTotal,
    adjustedSubtotal,
    totalBeforeTax,
    commissionableTotal,
    proposalVisibleLines,
  };
}

export function linesForPlacement(
  lines: FormatCalcLineDef[],
  placement: FormatCalcPlacement,
): FormatCalcLineDef[] {
  return sortLines(lines.filter((l) => l.placement === placement));
}

// Document Builder (DB-6): arithmetic formula_field expressions use
// `src/lib/document-builder/formulas/docFormulaEval.ts` — same round2
// discipline as placement-line evaluation above, scoped to document variables.
